import React, { useEffect, useState } from 'react'
import JumboListNoDataPlaceHolder from '@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder'
import useJumboAuth from '@jumbo/hooks/useJumboAuth'
import { Card, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import {
  addPresentation,
  copyPresentation,
  deletePresentation,
  getPresentations,
  refreshPresentation
} from 'app/services/api/presentation'
import { useTranslation } from 'react-i18next'
import PresentationItem from './PresentationItem'
import { useSnackbar } from 'notistack'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import { FE_URL } from 'app/services/config'
import JumboSearch from '@jumbo/components/JumboSearch'
import Div from '@jumbo/shared/Div'
import AddBoxIcon from '@mui/icons-material/AddBox'
import AddPresentationForm from './AddPresentationForm'
import { useNavigate } from 'react-router-dom'

const Presentations = ({ sx, selectorMode, onSelect }) => {
  const { getAuthUser } = useJumboAuth()
  const { customerId, userId } = getAuthUser()
  const { t } = useTranslation()
  const { showDialog, hideDialog } = useJumboDialog()
  const navigate = useNavigate()

  const [presentations, setPresentations] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchName, setSearchName] = useState()
  const { enqueueSnackbar } = useSnackbar()

  const loadPresentations = async () => {
    await getPresentations(customerId).then(presentations => {
      const filteredPresentations = searchName
        ? presentations.filter(presentation =>
            presentation.name.toLowerCase().includes(searchName)
          )
        : presentations
      setPresentations(filteredPresentations || [])
    })
    setLoading(false)
  }

  const handleAddPresentation = async ({ label }) => {
    hideDialog()
    setLoading(true)
    try {
      const presentationUrl = await addPresentation({
        label,
        customerId,
        userId
      })
      await loadPresentations()
      const message = `${t('buttons.add')} ${t('notifications.wasSuccessful')}`
      enqueueSnackbar(message, {
        variant: 'success'
      })
      navigate(`/presentation/${presentationUrl}`)
    } catch (error) {
      const message = `${t('buttons.add')} ${t('notifications.wasFailed')}`
      enqueueSnackbar(message, {
        variant: 'error'
      })
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    searchName !== undefined && loadPresentations()
  }, [searchName])

  const openModal = () => {
    showDialog({
      variant: 'default',
      title: t('pages.presentation.addPresentation'),
      sx: {
        '& .MuiDialog-container': {
          alignItems: 'flex-start'
        }
      },
      content: (
        <AddPresentationForm
          onAddPresentation={handleAddPresentation}
          onCancel={hideDialog}
        />
      )
    })
  }

  const handleCopy = async url => {
    setLoading(true)
    try {
      await copyPresentation(url)
      await loadPresentations()
      const message = `${t('buttons.copy')} ${t('notifications.wasSuccessful')}`
      enqueueSnackbar(message, {
        variant: 'success'
      })
    } catch (error) {
      const message = `${t('buttons.copy')} ${t('notifications.wasFailed')}`
      enqueueSnackbar(message, {
        variant: 'error'
      })
      setLoading(false)
    }
  }

  const handleRefresh = async url => {
    setLoading(true)
    try {
      await refreshPresentation(url)
      await loadPresentations()
      const message = `${t('buttons.update')} ${t(
        'notifications.wasSuccessful'
      )}`
      enqueueSnackbar(message, {
        variant: 'success'
      })
    } catch (error) {
      const message = `${t('buttons.update')} ${t('notifications.wasFailed')}`
      enqueueSnackbar(message, {
        variant: 'error'
      })
      setLoading(false)
    }
  }

  const handlePreview = async url => {
    window.open(FE_URL + '/presentation/' + url, '_blank', 'noreferrer')
  }

  const handleDelete = async url => {
    hideDialog()
    setLoading(true)
    try {
      await deletePresentation(url)
      await loadPresentations()
      const message = `${t('buttons.delete')} ${t(
        'notifications.wasSuccessful'
      )}`
      enqueueSnackbar(message, {
        variant: 'success'
      })
    } catch (error) {
      const message = `${t('buttons.delete')} ${t('notifications.wasFailed')}`
      enqueueSnackbar(message, {
        variant: 'error'
      })
      setLoading(false)
    }
  }

  return (
    <Div sx={sx}>
      <Typography variant={'h2'} mb={3}>
        {t('sidebar.menuItem.presentations')}
      </Typography>
      <Stack
        direction='row'
        display='flex'
        alignItems='center'
        mb={2}
        spacing={1}
      >
        <Card sx={{ mb: 0, width: '50%' }}>
          <JumboSearch
            page='presentations'
            onChange={setSearchName}
            sx={{
              width: '100%'
            }}
            placeholder={t('pages.presentation.searchPresentation')}
          />
        </Card>
        <Tooltip title={t('pages.presentation.addPresentation')}>
          <IconButton onClick={openModal}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {presentations && presentations.length ? (
        presentations.map((presentation, index) => (
          <PresentationItem
            key={index}
            item={presentation}
            disabled={loading}
            selectorMode={selectorMode}
            handleSelect={onSelect}
            handleCopy={handleCopy}
            handleRefresh={handleRefresh}
            handlePreview={handlePreview}
            handleDelete={url =>
              showDialog({
                variant: 'confirm',
                title: t('widgets.confirmDialog.areYouSure'),
                onYes: () => handleDelete(url),
                onNo: () => hideDialog()
              })
            }
          />
        ))
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </Div>
  )
}

export default Presentations
